import type { ComponentType } from 'react';

/**
 * How the chat presents one kind of draft when a bundle holds just that one
 * change. A plugin registers wording its users understand, and the card then
 * offers that action directly instead of the generic Review panel:
 *
 *   // workflows-ui/src/register.ts
 *   registerDraftPresentation('workflows.definition.create', {
 *     applyLabel: 'Create this workflow',
 *     appliedTitle: 'Workflow created as a draft',
 *     ...
 *   });
 *
 * Kinds nobody registers keep the generic card, so adding this to one plugin
 * changes nothing for the others.
 */
export interface DraftPresentation {
  /** The one button of a pending proposal: "Create this workflow". */
  applyLabel: string;
  /** Headline once applied: "Workflow created as a draft". */
  appliedTitle: string;
  /** One line under the headline: what happens next. */
  appliedNote?: string;
  /** Link to the changed record, built with the entity routes: "Open and publish". */
  openLabel?: string;
  /**
   * The proposal in sentences, shown under Details on the card itself. Without
   * it, Details opens the generic review panel (which shows the raw payload).
   */
  detailsOf?: (payload: Record<string, unknown>) => string[];
  /**
   * The proposal as the user will see it once made (a form, a document…), shown
   * in a dialog from the card's Preview button. Takes the place of Details.
   */
  Preview?: ComponentType<{ payload: Record<string, unknown> }>;
}

const REGISTRY = new Map<string, DraftPresentation>();

export const registerDraftPresentation = (draftKind: string, presentation: DraftPresentation): void => {
  REGISTRY.set(draftKind, presentation);
};

export const draftPresentationFor = (draftKind: string | undefined): DraftPresentation | undefined =>
  draftKind ? REGISTRY.get(draftKind) : undefined;

/** Test-only. */
export const __resetDraftPresentations = (): void => {
  REGISTRY.clear();
};
